<template>
  <PageBase class="login">
    <div class="login__wrapper columns is-mobile is-centered is-vcentered">
      <section class="login__inner column is-half-tablet is-4-desktop">
        <h3>{{ $t('LOGIN.TITLE') }}</h3>

        <div class="login__btn">
          <HdButton
            modifier="secondary"
            class="login__btn-connect"
            @click="redirectToLoginViaConnect"
            >{{ $t('GENERAL.CONNECT') }}</HdButton
          >
        </div>

        <p class="login__separator">{{ $t('GENERAL.OR') }}</p>

        <HdAlert v-if="error" type="error" :no-icon="false" class="login__error">
          {{ error }}
        </HdAlert>

        <HdDynamicForm
          :items="formItems"
          :submit-label="$t('LOGIN.LOGIN_BUTTON')"
          class="login__form"
          @submit="login"
        />

        <div class="login__link">
          <a
            href="https://app.homeday.de/crm_users/password/new"
            target="_blank"
            class="link login__link__inner"
          >
            {{ $t('LOGIN.FORGOT_PASSWORD') }}
          </a>
        </div>
      </section>
    </div>
  </PageBase>
</template>

<script>
// Components
import { HdAlert, HdButton, HdDynamicForm } from 'homeday-blocks';
// Route names
// Constants
import { ACTION } from '@/store/CONSTANTS';
import { REDIRECT_TO } from '@/config/QUERY_PARAMS';
import PageBase from '@/layout/PageBase.vue';
import { DEALS as WIZARD_ROUTE_NAMES } from '@/router/wizard/ROUTE_NAMES';

export default {
  name: 'LoginView',
  components: {
    HdDynamicForm,
    PageBase,
    HdAlert,
    HdButton,
  },
  data() {
    return {
      rememberMe: false,
      email: '',
      password: '',
      error: '',
      token: this.$route.query?.t,
    };
  },
  computed: {
    formItems() {
      return [
        {
          type: 'input',
          name: 'email',
          initialValue: this.email,
          props: {
            type: 'email',
            required: true,
            label: this.$t('FORM.EMAIL.LABEL_INFORMAL'),
            placeholder: this.$t('FORM.EMAIL.PLACEHOLDER'),
          },
        },
        {
          type: 'inputPassword',
          name: 'password',
          initialValue: this.password,
          props: {
            required: true,
            label: this.$t('FORM.PASSWORD.LABEL_INFORMAL'),
          },
        },
        {
          type: 'checkbox',
          name: 'rememberMe',
          initialValue: this.rememberMe,
          props: {
            required: false,
            innerLabel: this.$t('LOGIN.REMEMBER_ME'),
          },
        },
      ];
    },
  },
  created() {
    this.loginViaConnect();
  },
  methods: {
    login({ data, isValid }) {
      if (!isValid) {
        return;
      }

      this.$store
        .dispatch(ACTION.USER.LOGIN, data)
        .then(this.handleLoginRedirection)
        .catch(this.handleLoginError);
    },
    loginViaConnect() {
      const { token } = this;
      if (token) {
        this.$store
          .dispatch(ACTION.USER.LOGIN_VIA_CONNECT, { token })
          .then(this.handleLoginRedirection)
          .catch(this.handleLoginError);
      }
    },
    redirectToLoginViaConnect() {
      const connectBaseUrl = process.env.VUE_APP_HD_CONNECT;
      window.location = `${connectBaseUrl}/crm_users/sign_in?redirect_to=realtor_app`;
    },
    handleLoginRedirection() {
      // If the redirection param exists we go to that path
      // otherwise, we go to the deals overview
      const nextRoute = this.$route.query[REDIRECT_TO] || { name: WIZARD_ROUTE_NAMES.INDEX };
      this.$router.push(nextRoute);
    },
    handleLoginError({ response: { status, headers } = {} }) {
      this.error = '';
      this.$nextTick(() => this.setErrorFromStatus(status, headers));
    },
    setErrorFromStatus(status, headers) {
      switch (status) {
        case 429: {
          const seconds = Math.ceil(Number(headers?.['retry-after']));
          // Show seconds up to a minute and minutes up to an hour. Longer waits (daily IP window) get the generic text.
          if (seconds > 0 && seconds <= 60) {
            this.error = this.$t('LOGIN.TOO_MANY_ATTEMPTS', { seconds });
          } else if (seconds > 60 && seconds <= 3600) {
            this.error = this.$t('LOGIN.TOO_MANY_ATTEMPTS_MINUTES', { minutes: Math.ceil(seconds / 60) });
          } else {
            this.error = this.$t('LOGIN.TOO_MANY_ATTEMPTS_NO_WAIT');
          }
          break;
        }
        case 401:
          this.error = this.$t('LOGIN.CREDS_ERROR');
          break;

        default:
          this.error = this.$t('LOGIN.GENERIC_ERROR');
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';
.login {
  height: 100vh;

  &__wrapper {
    height: 100%;
    padding-top: $sp-l;
    padding-bottom: $sp-l;
  }

  h3 {
    text-align: center;
  }

  &__error {
    margin-bottom: $sp-m;
    animation: fadeIn 0.5s forwards;
  }

  &__form {
    margin-top: $sp-m;
    text-align: left;
  }

  &__btn {
    margin-top: $sp-m;
    &-connect {
      width: 100%;
    }
  }

  &__separator {
    margin-top: $sp-m;
    margin-bottom: $sp-m;
    @include font('DS-80');

    display: flex;
    flex-direction: row;
    color: $quaternary-color;

    &:before,
    &:after {
      content: '';
      flex: 1 1;
      border-bottom: 1px solid getShade($quaternary-color, 50);
      margin: auto;
    }
    &:before {
      margin-right: $sp-l;
    }
    &:after {
      margin-left: $sp-l;
    }
  }

  &__link {
    text-align: center;
    margin-top: $sp-m;

    &__inner {
      @include font('DS-100');
      display: inline-block;
    }
  }
}
</style>
